.dialog {
  /* *** A <dialog> IS NOT CENTERED BY DEFAULT ***
     showModal() puts it in the top layer, but the element itself still
     defaults to position: absolute with browser offsets — which pins it to
     the top-left corner. `position: fixed` + `inset: 0` + `margin: auto`
     is what actually centres it in the viewport. */
  position: fixed;
  inset: 0;
  margin: auto;

  padding: 0;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--bg-elevated);
  color: var(--text);
  box-shadow: var(--shadow-lg);

  /* Wider than before: at 520px the two-column form rows wrapped awkwardly
     and long labels ran out of room. */
  width: min(94vw, var(--modal-width, 640px));

  /* Leave breathing room top and bottom rather than filling the screen. */
  max-height: min(90vh, 900px);
  overflow: hidden;
}

/* On a phone the dialog should use the space it has rather than sit as a
   small floating card with wasted margins. */
@media (max-width: 640px) {
  .dialog {
    width: 100vw;
    max-width: 100vw;
    max-height: 100dvh;
    height: 100dvh;
    border: none;
    border-radius: 0;
  }
}

.dialog::backdrop {
  background: rgb(15 23 42 / 55%);
  backdrop-filter: blur(2px);
}

/* Widened: the old sizes forced two-column rows to wrap and clipped longer
   field labels. */
.sm { --modal-width: 480px; }
.md { --modal-width: 680px; }
.lg { --modal-width: 920px; }

/* Fills the dialog rather than repeating its max-height — the two had drifted
   apart, so the body's scroll box was sized against the wrong container. */
.content {
  display: flex;
  flex-direction: column;
  max-height: inherit;
  height: 100%;
  min-height: 0;   /* without this a flex child refuses to shrink and scroll */
}

/* Header and footer stay put; only the body between them scrolls. flex-shrink
   is what stops a long form from squeezing them off the screen. */
.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-5);
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
  background: var(--bg-elevated);
}

.title {
  font-size: var(--text-lg);
  font-weight: var(--weight-semibold);
}

.description {
  margin-top: var(--space-1);
  font-size: var(--text-sm);
  color: var(--text-muted);
  line-height: var(--leading-normal);
}

.close {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  font-size: 22px;
  line-height: 1;
  color: var(--text-muted);
  background: transparent;
  border: none;
  border-radius: var(--radius);
}

.close:hover {
  background: var(--bg-hover);
  color: var(--text);
}

.body {
  padding: var(--space-5);
  overflow-y: auto;   /* long forms scroll; the header and footer stay put */
  flex: 1 1 auto;
  min-height: 0;      /* the flex-child scroll rule again — see .content */
  overscroll-behavior: contain;  /* scrolling the form must not scroll the page */
}

.footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-5);
  border-top: 1px solid var(--border);
  background: var(--bg-sunken);
  flex-shrink: 0;
}

/* On a narrow screen stacked full-width buttons beat two cramped ones. */
@media (max-width: 480px) {
  .footer {
    flex-direction: column-reverse;
  }
  .footer > * {
    width: 100%;
  }
}

/* -------------------------------------------------------------- confirm */
.confirmMessage {
  color: var(--text-muted);
  line-height: var(--leading-normal);
}

.confirmActions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-5);
}

.cancelButton,
.primaryButton,
.dangerButton {
  padding: var(--space-2) var(--space-4);
  font-size: var(--text-base);
  font-weight: var(--weight-medium);
  border-radius: var(--radius);
  border: 1px solid transparent;
  min-height: 36px;
}

.cancelButton {
  background: var(--bg-elevated);
  color: var(--text);
  border-color: var(--border-strong);
}
.cancelButton:hover:not(:disabled) { background: var(--bg-hover); }

.primaryButton {
  background: var(--indigo);
  color: #fff;
}
.primaryButton:hover:not(:disabled) { background: var(--indigo-dark); }

.dangerButton {
  background: var(--danger);
  color: #fff;
}
.dangerButton:hover:not(:disabled) { filter: brightness(0.92); }

.cancelButton:disabled,
.primaryButton:disabled,
.dangerButton:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
